import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
    Box,
    Paper,
    Typography,
    Chip,
    Button,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    CircularProgress,
    Alert,
    TextField,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Pagination,
    IconButton,
    Tooltip,
    Stack,
    Grid
} from '@mui/material';
import {
    Receipt as InvoiceIcon,
    Visibility as ViewIcon,
    Download as DownloadIcon,
    Search as SearchIcon,
    FilterList as FilterIcon,
    Refresh as RefreshIcon,
    CalendarToday as CalendarIcon
} from '@mui/icons-material';
import DashboardLayout from '../../components/layouts/DashboardLayout';
import customerInvoiceService from '../../services/customerInvoiceService';
import pdfService from '../../services/pdfService';
import { format } from 'date-fns';

const CustomerInvoices = () => {
    const navigate = useNavigate();
    const location = useLocation();

    // State management
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [invoices, setInvoices] = useState([]);
    const [pagination, setPagination] = useState({
        current_page: 1,
        per_page: 10,
        total: 0,
        last_page: 1
    });

    // Filters
    const [filters, setFilters] = useState({
        status: '',
        from_date: '',
        to_date: '',
        search: ''
    });

    // Check for status filter in navigation state
    useEffect(() => {
        if (location.state?.filterStatus) {
            setFilters(prevFilters => ({
                ...prevFilters,
                status: location.state.filterStatus
            }));
        }
    }, [location.state]);

    // Load invoices on component mount and when filters or pagination changes
    useEffect(() => {
        loadInvoices();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [pagination.current_page, pagination.per_page, filters.status]);

    const loadInvoices = async () => {
        setLoading(true);
        setError(null);

        try {
            const params = {
                page: pagination.current_page,
                per_page: pagination.per_page,
                ...filters
            };

            const response = await customerInvoiceService.getMyInvoices(params);

            if (response.success) {
                setInvoices(response.data || []);
                if (response.meta) {
                    setPagination({
                        ...pagination,
                        current_page: response.meta.current_page || 1,
                        last_page: response.meta.last_page || 1,
                        total: response.meta.total || 0,
                        per_page: response.meta.per_page || 10
                    });
                }
            } else {
                setInvoices([]);
            }
        } catch (err) {
            console.error('Error loading invoices:', err);
            setError(err.response?.data?.message || 'Failed to load invoices');
            setInvoices([]);
        } finally {
            setLoading(false);
        }
    };

    const handleFilterChange = (field, value) => {
        setFilters({ ...filters, [field]: value });
    };

    const handleApplyFilters = () => {
        setPagination({ ...pagination, current_page: 1 });
        loadInvoices();
    };

    const handleClearFilters = () => {
        setFilters({
            status: '',
            from_date: '',
            to_date: '',
            search: ''
        });
        setPagination({ ...pagination, current_page: 1 });
        setTimeout(() => loadInvoices(), 100);
    };

    const handleViewInvoice = (invoice) => {
        // Navigate to invoice details page (reuse admin's invoice view)
        navigate(`/customer/invoices/${invoice.invoice_id}`);
    };

    const handleDownloadInvoice = async (invoice) => {
        try {
            // Fetch invoice details first
            const response = await customerInvoiceService.getInvoiceDetails(invoice.invoice_id);
            if (response.success) {
                const result = await pdfService.generateAndDownloadLegacyInvoice(response.data);
                if (!result.success) {
                    setError(result.message || 'Failed to generate PDF for download');
                }
            }
        } catch (err) {
            console.error('Error downloading invoice:', err);
            setError('Error generating PDF: ' + err.message);
        }
    };

    const getStatusColor = (status) => {
        if (!status) return 'default';

        switch (status.toLowerCase()) {
            case 'paid':
                return 'success';
            case 'pending':
                return 'warning';
            case 'overdue':
                return 'error';
            default:
                return 'default';
        }
    };

    const formatDate = (date) => {
        if (!date) return '-';
        try {
            return format(new Date(date), 'dd MMM yyyy');
        } catch (error) {
            console.error('Error formatting date:', date, error);
            return '-';
        }
    };

    const formatCurrency = (amount) => {
        return new Intl.NumberFormat('en-IN', {
            style: 'currency',
            currency: 'INR',
            minimumFractionDigits: 0
        }).format(amount || 0);
    };

    return (
        <DashboardLayout title="My Invoices" userType="customer">
            <Box sx={{ p: 3 }}>
                {/* Page Header */}
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                    <Typography variant="h4" sx={{ fontWeight: 600 }}>
                        My Invoices
                    </Typography>
                    <Button
                        variant="outlined"
                        startIcon={<RefreshIcon />}
                        onClick={loadInvoices}
                    >
                        Refresh
                    </Button>
                </Box>

                {/* Filters */}
                <Paper sx={{ p: 2, mb: 3 }}>
                    <Grid container spacing={2} alignItems="center">
                        <Grid item xs={12} sm={6} md={3}>
                            <TextField
                                fullWidth
                                label="Search Invoice No"
                                value={filters.search}
                                onChange={(e) => handleFilterChange('search', e.target.value)}
                                InputProps={{
                                    startAdornment: <SearchIcon sx={{ mr: 1, color: 'text.secondary' }} />
                                }}
                            />
                        </Grid>
                        <Grid item xs={12} sm={6} md={2}>
                            <FormControl fullWidth>
                                <InputLabel>Status</InputLabel>
                                <Select
                                    value={filters.status}
                                    onChange={(e) => handleFilterChange('status', e.target.value)}
                                    label="Status"
                                >
                                    <MenuItem value="">All</MenuItem>
                                    <MenuItem value="pending">Pending</MenuItem>
                                    <MenuItem value="paid">Paid</MenuItem>
                                    <MenuItem value="overdue">Overdue</MenuItem>
                                </Select>
                            </FormControl>
                        </Grid>
                        <Grid item xs={12} sm={6} md={2}>
                            <TextField
                                fullWidth
                                label="From Date"
                                type="date"
                                value={filters.from_date}
                                onChange={(e) => handleFilterChange('from_date', e.target.value)}
                                InputLabelProps={{ shrink: true }}
                            />
                        </Grid>
                        <Grid item xs={12} sm={6} md={2}>
                            <TextField
                                fullWidth
                                label="To Date"
                                type="date"
                                value={filters.to_date}
                                onChange={(e) => handleFilterChange('to_date', e.target.value)}
                                InputLabelProps={{ shrink: true }}
                            />
                        </Grid>
                        <Grid item xs={12} md={3}>
                            <Stack direction="row" spacing={1}>
                                <Button
                                    fullWidth
                                    variant="contained"
                                    startIcon={<FilterIcon />}
                                    onClick={handleApplyFilters}
                                >
                                    Apply
                                </Button>
                                <Button
                                    fullWidth
                                    variant="outlined"
                                    onClick={handleClearFilters}
                                >
                                    Clear
                                </Button>
                            </Stack>
                        </Grid>
                    </Grid>
                </Paper>

                {/* Error Alert */}
                {error && (
                    <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
                        {error}
                    </Alert>
                )}

                {/* Invoices Table */}
                <TableContainer component={Paper}>
                    {loading ? (
                        <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
                            <CircularProgress />
                        </Box>
                    ) : !invoices || invoices.length === 0 ? (
                        <Box sx={{ textAlign: 'center', p: 4 }}>
                            <InvoiceIcon sx={{ fontSize: 60, color: 'text.secondary', mb: 2 }} />
                            <Typography variant="h6" color="text.secondary">
                                No invoices found
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                                {filters.status || filters.search || filters.from_date
                                    ? 'Try adjusting your filters'
                                    : 'You don\'t have any invoices yet'}
                            </Typography>
                        </Box>
                    ) : (
                        <>
                            <Table>
                                <TableHead>
                                    <TableRow>
                                        <TableCell>Invoice No</TableCell>
                                        <TableCell>Invoice Date</TableCell>
                                        <TableCell>Due Date</TableCell>
                                        <TableCell>Status</TableCell>
                                        <TableCell align="right">Amount</TableCell>
                                        <TableCell>Shipments</TableCell>
                                        <TableCell align="right">Actions</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {invoices && invoices.length > 0 && invoices.map((invoice) => (
                                        <TableRow key={invoice.invoice_id} hover>
                                            <TableCell>
                                                <Typography variant="body2" fontWeight={600}>
                                                    {invoice.invoice_no}
                                                </Typography>
                                            </TableCell>
                                            <TableCell>
                                                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                                    <CalendarIcon sx={{ fontSize: 18, mr: 1, color: 'text.secondary' }} />
                                                    {formatDate(invoice.invoice_date)}
                                                </Box>
                                            </TableCell>
                                            <TableCell>
                                                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                                    <CalendarIcon sx={{ fontSize: 18, mr: 1, color: 'text.secondary' }} />
                                                    {formatDate(invoice.due_date)}
                                                </Box>
                                            </TableCell>
                                            <TableCell>
                                                <Chip
                                                    label={invoice.status ? invoice.status.toUpperCase() : 'UNKNOWN'}
                                                    color={getStatusColor(invoice.status)}
                                                    size="small"
                                                />
                                            </TableCell>
                                            <TableCell align="right">
                                                <Typography variant="body2" fontWeight={600}>
                                                    {formatCurrency(invoice.grand_total)}
                                                </Typography>
                                            </TableCell>
                                            <TableCell>
                                                {invoice.total_shipments} shipments
                                            </TableCell>
                                            <TableCell align="right">
                                                <Stack direction="row" spacing={1} justifyContent="flex-end">
                                                    <Tooltip title="View Invoice">
                                                        <IconButton
                                                            size="small"
                                                            color="primary"
                                                            onClick={() => handleViewInvoice(invoice)}
                                                        >
                                                            <ViewIcon />
                                                        </IconButton>
                                                    </Tooltip>
                                                    <Tooltip title="Download PDF">
                                                        <IconButton
                                                            size="small"
                                                            color="secondary"
                                                            onClick={() => handleDownloadInvoice(invoice)}
                                                        >
                                                            <DownloadIcon />
                                                        </IconButton>
                                                    </Tooltip>
                                                </Stack>
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>

                            {/* Pagination */}
                            {pagination.last_page > 1 && (
                                <Box sx={{ display: 'flex', justifyContent: 'center', p: 2 }}>
                                    <Pagination
                                        count={pagination.last_page}
                                        page={pagination.current_page}
                                        onChange={(e, page) => setPagination({ ...pagination, current_page: page })}
                                        color="primary"
                                    />
                                </Box>
                            )}
                        </>
                    )}
                </TableContainer>
            </Box>
        </DashboardLayout>
    );
};

export default CustomerInvoices;
